import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import { authApi, betsApi, walletApi, sessionBetsApi, tossBetsApi } from "../lib/api";
import { warmCasino } from "../lib/megaCache";

const AppContext = createContext(null);

const IDLE_LIMIT_MS = 15 * 60 * 1000; // auto-logout after 15 min of inactivity

export function AppProvider({ children }) {
  const [token, setToken] = useState(() => {
    // Force logout on load if the session has been idle beyond the limit.
    const tk = localStorage.getItem("cb_token");
    const last = Number(localStorage.getItem("cb_last_activity") || 0);
    if (tk && last && Date.now() - last > IDLE_LIMIT_MS) {
      localStorage.removeItem("cb_token");
      localStorage.removeItem("cb_user");
      return null;
    }
    return tk || null;
  });
  const [user, setUser] = useState(() => {
    if (!localStorage.getItem("cb_token")) return null;
    try { return JSON.parse(localStorage.getItem("cb_user")) || null; } catch { return null; }
  });
  const [balance, setBalance] = useState(0);
  const [onBet, setOnBet] = useState(0);
  const [bets, setBets] = useState([]);
  const [sessionBets, setSessionBets] = useState([]);
  const [tossBets, setTossBets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showBonusPopup, setShowBonusPopup] = useState(false);

  const isAuthed = !!token && !!user;

  // Header refresh button: wallet balance only (throws so the caller can toast on failure).
  const refreshBalance = useCallback(async () => {
    const wallet = await walletApi.get();
    setBalance(wallet.balance);
    setOnBet(wallet.onBet || 0);
    setTransactions(wallet.transactions || []);
    return wallet.balance;
  }, []);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem("cb_token")) return;
    try {
      const [wallet, betList, sessList, tossList] = await Promise.all([walletApi.get(), betsApi.list(), sessionBetsApi.list(), tossBetsApi.list()]);
      setBalance(wallet.balance);
      setOnBet(wallet.onBet || 0);
      setTransactions(wallet.transactions || []);
      setBets(betList || []);
      setSessionBets(sessList || []);
      setTossBets(tossList || []);
      authApi.me().then(({ user: u }) => {
        if (u) { setUser(u); localStorage.setItem("cb_user", JSON.stringify(u)); }
      }).catch(() => {});
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  }, []);

  useEffect(() => {
    if (token) refresh();
    warmCasino(); // prefetch Live Casino + slots catalogue and pre-decode artwork on boot/login
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = ({ token: tk, user: usr }, opts = {}) => {
    localStorage.setItem("cb_token", tk);
    localStorage.setItem("cb_user", JSON.stringify(usr));
    localStorage.setItem("cb_last_login", new Date().toISOString());
    localStorage.setItem("cb_last_activity", String(Date.now()));
    setToken(tk);
    setUser(usr);
    setBalance(usr.balance || 0);
    if (opts.newSignup) setShowBonusPopup(true);
  };

  const logout = () => {
    localStorage.setItem("cb_last_logout", new Date().toISOString());
    localStorage.removeItem("cb_token");
    localStorage.removeItem("cb_user");
    setToken(null);
    setUser(null);
    setBalance(0);
    setBets([]);
    setSessionBets([]);
    setTossBets([]);
    setTransactions([]);
  };

  // STRICT auto-logout after 15 min of inactivity.
  // Key rules: (1) if an interaction happens AFTER the limit already passed, log out
  // instead of resetting; (2) re-check when the tab regains visibility/focus (covers
  // background-tab timer throttling); (3) a precise deadline timer logs out a walked-away
  // user in the foreground. Activity writes are throttled to stay light.
  useEffect(() => {
    if (!isAuthed) return;
    let deadlineTimer = null;
    let lastWrite = 0;

    const getLast = () => Number(localStorage.getItem("cb_last_activity")) || Date.now();
    const expired = () => Date.now() - getLast() >= IDLE_LIMIT_MS;

    const schedule = () => {
      clearTimeout(deadlineTimer);
      const remaining = IDLE_LIMIT_MS - (Date.now() - getLast());
      deadlineTimer = setTimeout(() => {
        if (expired()) logout();
        else schedule();
      }, Math.max(1000, remaining));
    };

    const markActive = () => {
      if (expired()) { logout(); return; }          // strict: never revive a dead session
      const now = Date.now();
      if (now - lastWrite < 5000) return;            // throttle: at most one write/reschedule per 5s
      lastWrite = now;
      localStorage.setItem("cb_last_activity", String(now));
      schedule();
    };

    const onWake = () => {
      if (expired()) logout();
      else { schedule(); if (!document.hidden) refresh(); }
    };

    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    window.addEventListener("pageshow", onWake);

    localStorage.setItem("cb_last_activity", String(Date.now()));
    schedule();

    return () => {
      clearTimeout(deadlineTimer);
      events.forEach((e) => window.removeEventListener(e, markActive));
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      window.removeEventListener("pageshow", onWake);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  const withdraw = async (payload) => {
    try {
      const res = await walletApi.withdraw(payload);
      setBalance(res.balance);
      await refresh();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Could not submit withdrawal" };
    }
  };

  const placeBet = async (bet) => {
    try {
      const res = await betsApi.place(bet);
      setBalance(res.balance);
      await refresh();
      return { ok: true, ref: res.bet?.ref };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Could not place bet" };
    }
  };

  const cashOut = async (betId) => {
    try {
      const res = await betsApi.cashout(betId);
      setBalance(res.balance);
      await refresh();
      return { ok: true, hedge: res.hedge, lockedProfit: res.lockedProfit };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Could not cash out" };
    }
  };

  const placeSessionBet = async (payload) => {
    try {
      const res = await sessionBetsApi.place(payload);
      setBalance(res.balance);
      await refresh();
      return { ok: true, ref: res.bet?.ref, line: res.bet?.line };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Could not place session bet" };
    }
  };

  const placeTossBet = async (payload) => {
    try {
      const res = await tossBetsApi.place(payload);
      setBalance(res.balance);
      await refresh();
      return { ok: true, ref: res.bet?.ref };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Could not place toss bet" };
    }
  };

  const value = useMemo(() => ({
    user, isAuthed, login, logout,
    balance, onBet, withdraw,
    bets, placeBet, cashOut,
    sessionBets, placeSessionBet,
    tossBets, placeTossBet,
    transactions, refresh, refreshBalance,
    showBonusPopup, closeBonusPopup: () => setShowBonusPopup(false),
  }), [user, isAuthed, balance, onBet, bets, sessionBets, tossBets, transactions, refresh, showBonusPopup]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
